import { logger } from '@modules/logger';
import { isNullOrUndefined, isObjectLike } from '@modules/types';
import type { DBCard, DBCardSave } from 'types/db';
import { z } from 'zod';

export const DBCardEmailSignatureSchema = z.object({
  signature_id: z.string().optional(),
  qr_code: z.boolean().optional(),
  company_logo: z.boolean().optional(),
  profile_picture: z.boolean().optional(),
  headline: z.boolean().optional(),
  job_title: z.boolean().optional(),
  accreditations: z.boolean().optional(),
  org_name: z.boolean().optional(),
  phone_numbers: z.any().optional(),
  emails: z.any().optional(),
  urls: z.any().optional(),
  addresses: z.any().optional(),
  custom_banner: z.string().optional(),
});

export const DBCardSchema = z.object({
  card_id: z.string(),
  card_edit_id: z.string().optional().nullable(),
  blinq_message_enabled: z.boolean().optional(),
  where_we_met_enabled: z.boolean().optional(),
  is_invisible: z.boolean().optional(),
  is_paused: z.boolean().optional(),
  user_id: z.string().optional().nullable(),
  creation_timestamp_ms: z.number().optional().nullable(),
  updated_timestamp_ms: z.number().optional(),
  referral_link_enabled: z.boolean().optional(),
  bb_activation_timestamp_ms: z.number().optional().nullable(),
  card_title: z.string().optional(),
  org_id: z.string().optional(),
  color: z.string().optional(),
  first_name: z.string().optional(),
  middle_name: z.string().optional(),
  last_name: z.string().optional(),
  honorific_prefix: z.string().optional(),
  honorific_suffix: z.string().optional(),
  preferred_name: z.string().optional(),
  maiden_name: z.string().optional(),
  pronouns: z.string().optional(),
  job_title: z.string().optional(),
  org_name: z.string().optional(),
  department: z.string().optional(),
  accreditations: z.array(z.string()).optional(),
  headline: z.string().optional(),
  profile_picture_img: z.any().optional().nullable(),
  company_logo_img: z.any().optional().nullable(),
  cover_photo_img: z.any().optional().nullable(),
  card_preset_id: z.string().optional().nullable(),
  recipient_email: z.string().optional(),
  referrer_card_id: z.string().optional(),
  referrer_user_id: z.string().optional(),
  created_by: z.string().optional(),
  logo_is_in_qr: z.boolean().optional(),
  logo_in_qr_url: z.string().optional().nullable(),
  card_is_editable: z.boolean().optional(),
  all_field_types_locked: z.boolean().optional(),
  all_email_signature_fields_locked: z.boolean().optional(),
  color_locked: z.boolean().optional(),
  qr_code_locked: z.boolean().optional(),
  layout: z.string().optional(),
  locked_fields: z.array(z.string()).optional(),
  locked_field_types: z.array(z.string()).optional(),
  locked_email_signature_fields: z.array(z.string()).optional(),
  activation_email_sent_at: z.any().optional().nullable(),
  emails: z.array(z.any()).optional(),
  addresses: z.array(z.any()).optional(),
  urls: z.array(z.any()).optional(),
  phone_numbers: z.array(z.any()).optional(),
  user_profile_id: z.string().optional(),
  linked_attributes: z.array(z.string()).optional(),
  field_order: z.array(z.string()).optional(),
  email_signature: DBCardEmailSignatureSchema.optional(),
});

export function isDBCard(candidate: unknown): candidate is DBCard {
  try {
    DBCardSchema.parse(candidate);
  } catch (error) {
    logger.error(JSON.stringify(error));
  }

  return (
    isObjectLike(candidate) &&
    typeof candidate.card_id === 'string' &&
    (isNullOrUndefined(candidate.card_edit_id) || typeof candidate.card_edit_id === 'string') &&
    (candidate.blinq_message_enabled === undefined || typeof candidate.blinq_message_enabled === 'boolean') &&
    (candidate.where_we_met_enabled === undefined || typeof candidate.where_we_met_enabled === 'boolean') &&
    (candidate.is_invisible === undefined || typeof candidate.is_invisible === 'boolean') &&
    (candidate.is_paused === undefined || typeof candidate.is_paused === 'boolean') &&
    (isNullOrUndefined(candidate.user_id) || typeof candidate.user_id === 'string') &&
    (isNullOrUndefined(candidate.creation_timestamp_ms) ||
      typeof candidate.creation_timestamp_ms === 'number') &&
    (candidate.updated_timestamp_ms === undefined || typeof candidate.updated_timestamp_ms === 'number') &&
    (candidate.referral_link_enabled === undefined || typeof candidate.referral_link_enabled === 'boolean') &&
    (isNullOrUndefined(candidate.bb_activation_timestamp_ms) ||
      typeof candidate.bb_activation_timestamp_ms === 'number') &&
    (candidate.card_title === undefined || typeof candidate.card_title === 'string') &&
    (candidate.org_id === undefined || typeof candidate.org_id === 'string') &&
    (candidate.color === undefined || typeof candidate.color === 'string') &&
    (candidate.first_name === undefined || typeof candidate.first_name === 'string') &&
    (candidate.middle_name === undefined || typeof candidate.middle_name === 'string') &&
    (candidate.last_name === undefined || typeof candidate.last_name === 'string') &&
    (candidate.honorific_prefix === undefined || typeof candidate.honorific_prefix === 'string') &&
    (candidate.honorific_suffix === undefined || typeof candidate.honorific_suffix === 'string') &&
    (candidate.preferred_name === undefined || typeof candidate.preferred_name === 'string') &&
    (candidate.maiden_name === undefined || typeof candidate.maiden_name === 'string') &&
    (candidate.pronouns === undefined || typeof candidate.pronouns === 'string') &&
    (candidate.job_title === undefined || typeof candidate.job_title === 'string') &&
    (candidate.org_name === undefined || typeof candidate.org_name === 'string') &&
    (candidate.department === undefined || typeof candidate.department === 'string') &&
    (candidate.accreditations === undefined || Array.isArray(candidate.accreditations)) &&
    (candidate.headline === undefined || typeof candidate.headline === 'string') &&
    (isNullOrUndefined(candidate.profile_picture_img) || typeof candidate.profile_picture_img === 'object') &&
    (isNullOrUndefined(candidate.company_logo_img) || typeof candidate.company_logo_img === 'object') &&
    (isNullOrUndefined(candidate.cover_photo_img) || typeof candidate.cover_photo_img === 'object') &&
    (isNullOrUndefined(candidate.card_preset_id) || typeof candidate.card_preset_id === 'string') &&
    (candidate.recipient_email === undefined || typeof candidate.recipient_email === 'string') &&
    (candidate.referrer_card_id === undefined || typeof candidate.referrer_card_id === 'string') &&
    (candidate.referrer_user_id === undefined || typeof candidate.referrer_user_id === 'string') &&
    (candidate.created_by === undefined || typeof candidate.created_by === 'string') &&
    (candidate.logo_is_in_qr === undefined || typeof candidate.logo_is_in_qr === 'boolean') &&
    (isNullOrUndefined(candidate.logo_in_qr_url) || typeof candidate.logo_in_qr_url === 'string') &&
    (candidate.card_is_editable === undefined || typeof candidate.card_is_editable === 'boolean') &&
    (candidate.all_field_types_locked === undefined ||
      typeof candidate.all_field_types_locked === 'boolean') &&
    (candidate.all_email_signature_fields_locked === undefined ||
      typeof candidate.all_email_signature_fields_locked === 'boolean') &&
    (candidate.color_locked === undefined || typeof candidate.color_locked === 'boolean') &&
    (candidate.qr_code_locked === undefined || typeof candidate.qr_code_locked === 'boolean') &&
    (isNullOrUndefined(candidate.layout) || typeof candidate.layout === 'string') &&
    (candidate.locked_fields === undefined || Array.isArray(candidate.locked_fields)) &&
    (candidate.locked_field_types === undefined || Array.isArray(candidate.locked_field_types)) &&
    (candidate.locked_email_signature_fields === undefined ||
      Array.isArray(candidate.locked_email_signature_fields)) &&
    (candidate.activation_email_sent_at === undefined || isObjectLike(candidate.activation_email_sent_at)) &&
    (candidate.emails === undefined || Array.isArray(candidate.emails)) &&
    (candidate.addresses === undefined || Array.isArray(candidate.addresses)) &&
    (candidate.urls === undefined || Array.isArray(candidate.urls)) &&
    (candidate.phone_numbers === undefined || Array.isArray(candidate.phone_numbers)) &&
    (candidate.user_profile_id === undefined || typeof candidate.user_profile_id === 'string') &&
    (candidate.linked_attributes === undefined || Array.isArray(candidate.linked_attributes)) &&
    (candidate.field_order === undefined || Array.isArray(candidate.field_order)) &&
    (isObjectLike(candidate.email_signature) || candidate.email_signature === undefined) &&
    (candidate.email_signature?.signature_id === undefined ||
      typeof candidate.email_signature?.signature_id === 'string') &&
    (candidate.email_signature?.qr_code === undefined ||
      typeof candidate.email_signature?.qr_code === 'boolean') &&
    (candidate.email_signature?.company_logo === undefined ||
      typeof candidate.email_signature?.company_logo === 'boolean') &&
    (candidate.email_signature?.profile_picture === undefined ||
      typeof candidate.email_signature?.profile_picture === 'boolean') &&
    (candidate.email_signature?.headline === undefined ||
      typeof candidate.email_signature?.headline === 'boolean') &&
    (candidate.email_signature?.job_title === undefined ||
      typeof candidate.email_signature?.job_title === 'boolean') &&
    (candidate.email_signature?.accreditations === undefined ||
      typeof candidate.email_signature?.accreditations === 'boolean') &&
    (candidate.email_signature?.org_name === undefined ||
      typeof candidate.email_signature?.org_name === 'boolean') &&
    (candidate.email_signature?.phone_numbers === undefined ||
      isObjectLike(candidate.email_signature?.phone_numbers)) &&
    (candidate.email_signature?.emails === undefined || isObjectLike(candidate.email_signature?.emails)) &&
    (candidate.email_signature?.urls === undefined || isObjectLike(candidate.email_signature?.urls)) &&
    (candidate.email_signature?.addresses === undefined ||
      isObjectLike(candidate.email_signature?.addresses)) &&
    (candidate.email_signature?.custom_banner === undefined ||
      typeof candidate.email_signature?.custom_banner === 'string')
  );
}

export function isDBCardSave(candidate: unknown): candidate is DBCardSave {
  return (
    isObjectLike(candidate) &&
    typeof candidate.card_id === 'string' &&
    typeof candidate.card_edit_id === 'string' &&
    (candidate.saved_at === undefined || isObjectLike(candidate.saved_at)) &&
    (candidate.timestamp_ms === undefined || typeof candidate.timestamp_ms === 'number') &&
    (candidate.org_id === undefined || typeof candidate.org_id === 'string') &&
    (candidate.location === undefined || typeof candidate.location === 'string')
  );
}

export function isResendActivationResponse(candidate: unknown): candidate is { is_successful: boolean } {
  return isObjectLike(candidate) && typeof candidate.is_successful === 'boolean';
}

export const dBCardAddressSchema = z.object({
  type: z.string().nullable().optional(),
  label: z.string().optional(),
  value: z.string().optional(),
  id: z.string().nullable().optional(),
  is_preset: z.boolean().nullable().optional(),
  primary: z.boolean().nullable().optional(),
  linked_to: z.string().nullable().optional(),
});

export const dBCardEmailSchema = z.object({
  label: z.string().optional(),
  value: z.string().optional(),
  id: z.string().nullable().optional(),
  is_preset: z.boolean().nullable().optional(),
  primary: z.boolean().nullable().optional(),
  linked_to: z.string().nullable().optional(),
});

export const dBCardPhoneNumberSchema = z.object({
  label: z.string().optional(),
  value: z.string().optional(),
  extension: z.string().optional(),
  country_code: z.string().nullable().optional(),
  id: z.string().nullable().optional(),
  is_preset: z.boolean().nullable().optional(),
  primary: z.boolean().nullable().optional(),
  linked_to: z.string().nullable().optional(),
});

export const dBCardUrlSchema = z.object({
  label: z.string().optional(),
  title: z.string().optional(),
  type: z.string().nullable().optional(),
  value: z.string().optional(),
  id: z.string().nullable().optional(),
  is_preset: z.boolean().nullable().optional(),
  linked_to: z.string().nullable().optional(),
  user_value: z.string().nullable().optional(),
});
